import React, { useRef, useEffect, useState } from 'react';
import { ServiceStatus, AIConfig, TestResult } from '../../types';
import CollapsibleCell from '../common/CollapsibleCell';

interface SystemStatusWidgetProps {
    mode: 'environment' | 'logs' | 'results';
    onClose?: () => void;
    // Environment props
    status?: ServiceStatus | null;
    aiConfig?: AIConfig | null;
    // Logs props
    logs?: string[];
    logFilters?: { [key: string]: boolean };
    onLogFilterToggle?: (filterName: string) => void;
    // Results props
    results?: Map<string, TestResult>;
    selectedTest?: string;
    setSelectedTest?: (testName: string) => void;
    selectedAgent?: string;
}



const SystemStatusWidget: React.FC<SystemStatusWidgetProps> = ({
    mode,
    status,
    aiConfig,
    logs = [],
    logFilters = {},
    onLogFilterToggle,
    onClose,
    results = new Map(),
    selectedTest,
    setSelectedTest,
    selectedAgent
}) => {

    const logsEndRef = useRef<HTMLPreElement>(null);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout>();

    // Format service names for environment display
    const formatServiceName = (key: string) => {
        let formatted = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        if (formatted === 'Db') formatted = 'DB';
        if (formatted === 'Ai') formatted = 'AI';
        if (formatted.startsWith('Ai')) formatted = formatted.replace(/^Ai/, 'AI');
        return formatted;
    };

    const [filtersExpanded, setFiltersExpanded] = useState(false);


    // Status checking function from ExecutionView
    const checkIndividualStatus = (key: string, s: any): boolean => {
        // Standard positive values
        if (s === 'ok' || s === 'true' || s === true || s === 'connected' || s === 'ready') {
            return true;
        }

        // Environment: Check comprehensive status
        if (key === 'environment' && typeof s === 'object' && s !== null) {
            const isOk = (s as any).overall === 'ready' || (s as any).overall === 'partial';
            return isOk;
        }

        // Service URLs: Non-null object means configured
        if (key === 'service_urls' && typeof s === 'object' && s !== null) {
            return true;
        }

        // AI Provider: Valid provider names
        if (key === 'ai_provider' && typeof s === 'string' &&
            ['openai', 'anthropic', 'google'].includes(s.toLowerCase())) {
            return true;
        }

        return false;
    };

    // Status pill component
    const StatusPill = ({ name, s }: { name: string; s: string | boolean | undefined }) => {
        const isPositive = checkIndividualStatus(name.toLowerCase().replace(/ /g, '_'), s);

        return (
            <div className="status-pill">
                <span
                    className="status-dot"
                    style={{
                        backgroundColor: isPositive ? '#38a169' : '#e53e3e'
                    }}
                ></span>
                <span>{name}</span>
            </div>
        );
    };

    // Get status color for results
    const getStatusColor = (status: string | undefined) => ({
        'PASSED': '#28a745',
        'FAILED': '#dc3545',
        'VALIDATION_ERROR': '#ffc107',
        'RUNNING': '#17a2b8',
        'NEW': '#6c757d'
    }[status || 'NEW']);

    // Filter logs based on active filters
    const filteredLogs = logs.filter(log => {
        const serviceName = Object.keys(logFilters).find(service => log.includes(`[${service}]`));
        return !serviceName || logFilters[serviceName as keyof typeof logFilters];
    });

    // Auto-scroll to bottom when logs change (only if user not scrolling)
    useEffect(() => {
        if (logsEndRef.current && mode === 'logs' && !isUserScrolling) {
            logsEndRef.current.scrollTop = logsEndRef.current.scrollHeight;
        }
    }, [filteredLogs, mode, isUserScrolling]);

    const handleScroll = (e: React.UIEvent<HTMLPreElement>) => {
        const element = e.currentTarget;
        const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 50;

        if (!isAtBottom) {
            setIsUserScrolling(true);

            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = setTimeout(() => {
                setIsUserScrolling(false);
            }, 5000);
        } else {
            setIsUserScrolling(false);
        }
    };

    if (mode === 'environment') {
        return (
            <div className="dashboard-widget">
                <h3>Environment Status</h3>
                {status ? (
                    <div className="status-list">
                        {Object.entries(status)
                            .filter(([key]) => key.toLowerCase() !== 'environment')
                            .map(([key, value]) => (
                                <StatusPill
                                    key={key}
                                    name={formatServiceName(key)}
                                    s={value}
                                />
                            ))}
                    </div>
                ) : (
                    <p style={{ fontSize: '1rem', color: '#666', textAlign: 'center', padding: '2rem' }}>
                        Checking system health...
                    </p>
                )}

                {aiConfig && (
                    <div style={{
                        marginTop: '2rem',
                        padding: '1rem',
                        backgroundColor: 'rgba(248, 249, 250, 0.9)',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        border: '1px solid rgba(0,0,0,0.1)'
                    }}>
                        <div style={{
                            fontSize: '1rem',
                            marginBottom: '0.5rem',
                            fontWeight: '600',
                            color: '#2d3748'
                        }}>
                            <strong>AI Model:</strong> {aiConfig.default_model}
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: '600' }}>
                            <strong>Configured:</strong>{' '}
                            {aiConfig.testwerk_api_key_configured ? (
                                <span style={{ color: '#28a745', marginLeft: '0.5rem', fontWeight: 'bold' }}>Yes</span>
                            ) : (
                                <span style={{ color: '#dc3545', marginLeft: '0.5rem', fontWeight: 'bold' }}>No</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (mode === 'logs') {
        return (
            <div className="dashboard-widget" style={{ height: '100%', display: 'flex', flexDirection: 'column', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>


                {/* Log Filters */}
                {/* Log Filters with Collapse/Expand */}
                {Object.keys(logFilters).length > 0 && (
                    <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'nowrap' }}>
                            <span style={{ fontSize: '0.9rem', fontWeight: '500', minWidth: 'fit-content' }}>
                                Agent: <strong>{selectedAgent}</strong>
                            </span>
                            <span style={{ fontSize: '0.9rem', color: '#666', minWidth: 'fit-content' }}>
                                Lines: {filteredLogs.length}
                            </span>
                            <button
                                onClick={() => setFiltersExpanded(!filtersExpanded)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    fontSize: '0.9rem',
                                    color: '#666',
                                    padding: '0.2rem 0.4rem',
                                    borderRadius: '4px',
                                    minWidth: 'fit-content'
                                }}
                            >
                                <span style={{
                                    transform: filtersExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                    fontSize: '0.8rem'
                                }}>
                                    ▶
                                </span>
                                Filters
                            </button>
                        </div>

                        {filtersExpanded && (
                            <div style={{
                                display: 'flex',
                                gap: '0.5rem',
                                flexWrap: 'wrap',
                                marginTop: '0.5rem',
                                paddingLeft: '1rem'
                            }}>
                                {Object.entries(logFilters).map(([filter, enabled]) => (
                                    <label key={filter} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                        padding: '0.2rem 0.4rem',
                                        borderRadius: '4px',
                                        backgroundColor: enabled ? '#e8f5e8' : '#f5f5f5',
                                        border: '1px solid #ddd',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={enabled}
                                            onChange={() => onLogFilterToggle?.(filter)}
                                            style={{ margin: 0 }}
                                        />
                                        {filter.replace(/_/g, ' ')}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Logs Display */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <pre
                        ref={logsEndRef}
                        className="logs-box"
                        style={{ flex: 1, margin: 0, overflow: 'auto' }}
                        onScroll={handleScroll}
                    >
                        {filteredLogs.length > 0 ? filteredLogs.join('\n') : ':::::::::Awaiting Start:::::::::'}
                    </pre>

                </div>
            </div>
        );
    }

    if (mode === 'results') {
        const resultsArray = Array.from(results.values());
        const passedCount = resultsArray.filter(r => r.status === 'PASSED').length;
        const failedCount = resultsArray.filter(r => r.status === 'FAILED').length;

        return (
            <div className="dashboard-widget" style={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>

                {/* Results Summary */}
                <div style={{
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '0.85rem',
                    padding: '0.5rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px'
                }}>
                    <span>
                        <strong>Agent:</strong> {selectedAgent}
                    </span>
                    <span>
                        <strong>Total:</strong> {results.size} |
                        <strong style={{ color: '#28a745', marginLeft: '0.5rem' }}>
                            Passed: {passedCount}
                        </strong> |
                        <strong style={{ color: '#dc3545', marginLeft: '0.5rem' }}>
                            Failed: {failedCount}
                        </strong>
                    </span>
                </div>

                {/* Results Table */}
                <div style={{ flex: 1, overflow: 'auto' }}>
                    <table className="results-table">
                        <thead>
                            <tr>
                                <th style={{ width: '10%', textAlign: 'center' }}>Test Name</th>
                                <th style={{ width: '5%', textAlign: 'center' }}>Status</th>
                                <th style={{ width: '15%', textAlign: 'center' }}>User Prompt</th>
                                <th style={{ width: '15%', textAlign: 'center' }}>Test Data</th> {/* ADDED */}
                                <th style={{ width: '15%', textAlign: 'center' }}>Agent Output</th>
                                <th style={{ width: '15%', textAlign: 'center' }}>Expected</th>
                                <th style={{ width: '15%', textAlign: 'center' }}>AI Analysis</th>
                                <th style={{ width: '5%', textAlign: 'center' }}>Confidence</th>
                            </tr>
                        </thead>
                        <tbody>
                            {resultsArray.sort((a, b) => a.test_name.localeCompare(b.test_name)).map((r) => (
                                <tr
                                    key={r.test_name}
                                    onClick={() => setSelectedTest?.(r.test_name)}
                                    className={selectedTest === r.test_name ? 'selected-row' : ''}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {/* 1. Test Name */}
                                    <td style={{ width: '10%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {r.test_name}
                                    </td>

                                    {/* 2. Status */}
                                    <td style={{ width: '5%', textAlign: 'center' }}>
                                        <span
                                            className="status-badge"
                                            style={{
                                                backgroundColor: getStatusColor(r.status),
                                                padding: '0.25rem 0.5rem',
                                                borderRadius: '12px',
                                                color: 'white',
                                                fontSize: '0.7rem',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {r.status}
                                        </span>
                                    </td>

                                    {/* 3. User Prompt (THE MISSING CELL) */}
                                    <td style={{ width: '15%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {r.task_prompt}
                                    </td>

                                    {/* 4. Mock Data (THE NEW CELL) */}
                                    <td style={{ width: '15%' }}>
                                        <CollapsibleCell content={r.mock_data ? JSON.stringify(r.mock_data, null, 2) : '::::'}
                                            summaryText="View Data"
                                        />
                                    </td>

                                    {/* 5. Agent Output */}
                                    <td style={{ width: '15%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {r.output}
                                    </td>

                                    {/* 6. Expected Outcome */}
                                    <td style={{ width: '15%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {r.expected_outcome}
                                    </td>

                                    {/* 7. AI Analysis */}
                                    <td style={{ width: '15%' }}>
                                        <CollapsibleCell content={r.validation_analysis || ''} />
                                    </td>

                                    {/* 8. Confidence */}
                                    <td style={{ width: '5%', textAlign: 'center' }}>
                                        {r.ai_confidence ? (
                                            <span style={{
                                                color: r.ai_confidence > 0.8 ? '#28a745' : r.ai_confidence > 0.6 ? '#ffc107' : '#dc3545',
                                                fontWeight: 'bold'
                                            }}>
                                                {(r.ai_confidence * 100).toFixed(0)}%
                                            </span>
                                        ) : 'N/A'}
                                    </td>
                                </tr>
                            ))}

                            {results.size === 0 && (
                                <tr>
                                    {/* Update colspan to 7 */}
                                    <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                                        No tests have been executed yet.
                                        <br />
                                        <small>Click "Execute Tests" to start AI-driven testing.</small>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return null;
};

export default SystemStatusWidget;